import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Zap,
    Target,
    ShieldAlert,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    RefreshCw,
    Search,
    ChevronDown,
    ChevronUp,
    DollarSign,
    Activity,
    Layers,
    Edit3,
    Flame,
    Award,
    Calendar,
    BarChart2,
    Info,
    X
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { authFetch } from '../utils/apiClient';

const BACKEND_URL = process.env.REACT_APP_NOMMIA_API_URL || 'https://nommia-crm-backend-production.up.railway.app';
const XVALLEY_BRIDGE_URL = 'https://innovative-rebirth-production.up.railway.app';

const DEFAULT_10X_PAMM_MASTERS = {
    '20068': 'Alpha 10X',
    '20069': 'Gold Crown 10X',
    '20070': 'Gama 10X',
    '20567': 'Infinity 10X',
    '20633': 'Nero 10X',
    '20634': 'Beta 10X',
    '20750': 'Sigma 10X'
};

const PIONEER_PRICE_BY_SIZE = {
    5000: 49,
    7500: 69,
    10000: 89,
    25000: 179,
    50000: 299,
    100000: 549,
    200000: 999
};

function parseSizeFromName(name = '') {
    const str = String(name || '');
    const kMatch = str.match(/\$?\s*(\d+(?:\.\d+)?)\s*k\b/i);
    if (kMatch && kMatch[1]) {
        const val = parseFloat(kMatch[1]) * 1000;
        if (val >= 100) return val;
    }
    const dollarMatch = str.match(/\$\s*([0-9]{1,3}(?:,[0-9]{3})+|[0-9]{3,7})(?!\s*k\b)/i);
    if (dollarMatch && dollarMatch[1]) {
        const val = parseFloat(dollarMatch[1].replace(/,/g, ''));
        if (val >= 100) return val;
    }
    return 0;
}

function formatUsd(val, decimals = 2) {
    const num = Number(val || 0);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function pickDefined() {
    for (let i = 0; i < arguments.length; i++) {
        if (arguments[i] !== undefined && arguments[i] !== null) {
            return arguments[i];
        }
    }
    return undefined;
}

/**
 * Direct Supabase + XValley Master-Proxy fallback builder so the CRM page works 100% live immediately
 * even if the Railway backend hasn't finished redeploying yet.
 */
async function buildDirectSupabaseFallback() {
    const [pammAllocsRes, hwmRes, lpWithdrawalsRes, pioneerClaimsRes, usersRes, activePammsRes, clientMetricsRes] = await Promise.all([
        supabase.from('pamm_live_allocations').select('*').limit(3000),
        supabase.from('pamm_hwm_tracking').select('*').limit(3000),
        supabase.from('10xlp_withdrawals').select('*').limit(1000),
        supabase.from('pioneer_live_claims').select('*').limit(1000),
        supabase.from('user_broker_accounts').select('user_id, email, referral_code, referred_by, partner_id').limit(5000),
        supabase.from('active_pamms').select('account_id, strategy_name, strategy_provider_email').limit(200),
        supabase.from('client_metrics').select('email, balance, equity, total_deposits, 10x_deposits, pamm_deposits').limit(3000)
    ]);

    const pammAllocs = pammAllocsRes.data || [];
    const hwmRows = hwmRes.data || [];
    const lpWithdrawals = lpWithdrawalsRes.data || [];
    const pioneerClaims = pioneerClaimsRes.data || [];
    const users = usersRes.data || [];
    const activePamms = activePammsRes.data || [];
    const clientMetrics = clientMetricsRes.data || [];
    const tradersList = [];
    const seenTraderIds = new Set();
    const addTraders = (arr) => {
        if (!Array.isArray(arr)) return;
        arr.forEach(t => {
            const id = String((t && (t.I || t.Id)) || '');
            if (id && !seenTraderIds.has(id)) {
                seenTraderIds.add(id);
                tradersList.push(t);
            }
        });
    };

    // Fetch cached traders list from master-proxy in a single call (served from backend RAM with 0 XValley WAMP load)
    try {
        const allRes = await fetch(`${XVALLEY_BRIDGE_URL}/api/master-proxy/accounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ all: true })
        });
        if (allRes && allRes.ok) {
            const allJson = await allRes.json();
            const allArr = Array.isArray(allJson) ? allJson : (allJson && Array.isArray(allJson.accounts) ? allJson.accounts : []);
            addTraders(allArr);
        }
    } catch (_) {
        // Ignore fallback error
    }

    const traderById = {};
    const liveChildSlavesByParentId = {};
    tradersList.forEach(t => {
        const id = String(t.I || t.Id || '');
        if (id) {
            traderById[id] = t;
            if (t.SLV === true) {
                const pId = String(t.ParentId || t.PI || t.PID || '');
                const slaveLiveEq = Math.max(0, parseFloat(pickDefined(t.E, t.C, t.BAL, 0)));
                if (pId && slaveLiveEq > 1.0) {
                    liveChildSlavesByParentId[pId] = (liveChildSlavesByParentId[pId] || 0) + 1;
                }
            }
        }
    });

    const isTrader10xAccount = (t) => {
        if (!t) return false;
        const nLower = String(t.Name || '').toLowerCase();
        const taldObj = t.TALD || {};
        const tatdObj = t.TATD || {};
        const gLower = String(t.Group || taldObj.Name || taldObj.N || tatdObj.Name || '').toLowerCase();
        return nLower.includes('10x') || gLower.includes('10x');
    };

    const isArchivedBook2Root10x = (t, accId) => {
        if (!t) return false;
        const idStr = String(accId || t.I || t.Id || '');
        const hasLiveChildSlaves = (liveChildSlavesByParentId[idStr] || 0) > 0;
        if (hasLiveChildSlaves) return false;
        return t.A === false && Number(t.B) === 2;
    };

    const clientMetricsByEmail = {};
    clientMetrics.forEach(cm => {
        const em = (cm.email || '').toLowerCase().trim();
        if (em) clientMetricsByEmail[em] = cm;
    });

    const pamm10xMasters = { ...DEFAULT_10X_PAMM_MASTERS };
    const isNumericMasterId = (val) => /^\d+$/.test(String(val || '').trim());
    activePamms.forEach(p => {
        if (isNumericMasterId(p.account_id) && String(p.strategy_name || '').toLowerCase().includes('10x')) {
            pamm10xMasters[String(p.account_id).trim()] = p.strategy_name;
        }
    });
    pammAllocs.forEach(s => {
        if (isNumericMasterId(s.master_id) && String(s.master_name || '').toLowerCase().includes('10x') && !pamm10xMasters[String(s.master_id).trim()]) {
            pamm10xMasters[String(s.master_id).trim()] = s.master_name;
        }
    });
    hwmRows.forEach(h => {
        if (isNumericMasterId(h.strategy_id) && String(h.strategy_name || '').toLowerCase().includes('10x') && !pamm10xMasters[String(h.strategy_id).trim()]) {
            pamm10xMasters[String(h.strategy_id).trim()] = h.strategy_name;
        }
    });
    tradersList.forEach(t => {
        const id = String(t.I || t.Id || '');
        const tName = String(t.Name || '').trim();
        if (id && t.MAM === true && tName.toLowerCase().includes('10x') && !pamm10xMasters[id]) {
            pamm10xMasters[id] = tName;
        }
    });

    const userMetaByEmail = {};
    const codeToPartnerEmail = {};
    users.forEach(u => {
        const em = (u.email || '').toLowerCase().trim();
        if (!em) return;
        userMetaByEmail[em] = u;
        if (u.referral_code) {
            codeToPartnerEmail[String(u.referral_code).toUpperCase().trim()] = em;
        }
    });

    const hwmMap = {};
    hwmRows.forEach(h => {
        const em = (h.user_email || '').toLowerCase().trim();
        const sId = String(h.strategy_id || '');
        if (em && sId) hwmMap[`${em}_${sId}`] = parseFloat(h.allocated_capital || 0);
    });

    // 1. Build 10X & Standard PAMM Pools
    const computePool = (mId, mName) => {
        const masterTrader = traderById[mId];
        const slaves = pammAllocs.filter(s => String(s.master_id) === mId);
        let posClosedEqSum = 0;
        let activeAllocCapSum = 0;
        let activeSlavesCount = 0;

        slaves.forEach(s => {
            const em = (s.email || '').toLowerCase().trim();
            const hwmCap = hwmMap[`${em}_${mId}`];
            const isWithdrawn = hwmCap !== undefined && hwmCap <= 0;
            const slaveT = traderById[String(s.slave_id)];
            const eq = slaveT ? Math.max(0, parseFloat(pickDefined(slaveT.E, slaveT.C, slaveT.BAL, s.pamm_live_equity, 0))) : Math.max(0, parseFloat(s.pamm_live_equity) || 0);
            const alloc = isWithdrawn ? 0 : (parseFloat(s.allocation_amount) || parseFloat(s.pamm_deposit) || 0);
            if (!isWithdrawn && (eq > 0 || alloc > 0)) {
                activeSlavesCount++;
                if (eq > 0) posClosedEqSum += eq;
                if (alloc > 0) activeAllocCapSum += alloc;
            }
        });

        // In XValley PAMM architecture:
        // - `posClosedEqSum` = True Closed Equity (Closed Balance) of the PAMM pool (sum of active slave closed equities)
        // - `activeAllocCapSum` = Total Active Deposits / Allocated Capital added to the pool
        // - `masterTrader.E` = Live Equity (Open Balance) of the pool including open floating trades
        // (Note: `masterTrader.C` is XValley's cumulative net capital before closed losses, NOT closed equity)
        let poolClosedBal = posClosedEqSum;
        let poolLiveEq = posClosedEqSum;
        if (masterTrader) {
            const rawMasterEq = Math.max(0, parseFloat(pickDefined(masterTrader.E, 0)));
            if (poolClosedBal <= 0) {
                poolClosedBal = Math.max(0, parseFloat(pickDefined(masterTrader.BAL, 0)));
            }
            poolLiveEq = rawMasterEq;
        }

        const openPnlUsd = poolLiveEq - poolClosedBal;
        const openDdUsd = Math.max(0, poolClosedBal - poolLiveEq);
        const openDdPct = poolClosedBal > 0 ? (openDdUsd / poolClosedBal) * 100 : 0;
        const openPnlPct = poolClosedBal > 0 ? (openPnlUsd / poolClosedBal) * 100 : 0;

        return {
            master_id: mId,
            master_name: mName,
            pool_deposits: Number(activeAllocCapSum.toFixed(2)),
            active_allocated_capital: Number(activeAllocCapSum.toFixed(2)),
            pool_closed_equity: Number(poolClosedBal.toFixed(2)),
            pool_closed_balance: Number(poolClosedBal.toFixed(2)),
            pool_live_equity: Number(poolLiveEq.toFixed(2)),
            open_floating_pnl_usd: Number(openPnlUsd.toFixed(2)),
            open_floating_pnl_pct: Number(openPnlPct.toFixed(2)),
            open_floating_drawdown_usd: Number(openDdUsd.toFixed(2)),
            open_floating_drawdown_pct: Number(openDdPct.toFixed(2)),
            active_slaves_count: activeSlavesCount,
            total_slaves_count: slaves.length,
            is_wiped_out: activeAllocCapSum > 0 && poolLiveEq < Math.max(50, activeAllocCapSum * 0.05)
        };
    };

    const pammPoolsMap = {};
    Object.entries(pamm10xMasters).forEach(([mId, mName]) => {
        pammPoolsMap[mId] = computePool(mId, mName);
    });
    const allPoolsMap = { ...pammPoolsMap };
    pammAllocs.forEach(s => {
        const mId = String(s.master_id || '');
        if (mId && !allPoolsMap[mId]) {
            allPoolsMap[mId] = computePool(mId, s.master_name || 'Standard PAMM');
        }
    });

    // 2. Group both 10X PAMM Slaves AND Legacy Standard PAMM Slaves by Client Email with Open Drawdown Apportionment
    const slaves10xByEmail = {};
    const allSlavesByEmail = {};
    pammAllocs.forEach(s => {
        const mId = String(s.master_id || '');
        if (!mId) return;
        const em = (s.email || '').toLowerCase().trim();
        if (!em) return;

        const is10xStrategy = Boolean(pamm10xMasters[mId]);
        const hwmCap = hwmMap[`${em}_${mId}`];
        const isWithdrawn = hwmCap !== undefined && hwmCap <= 0;
        const slaveTrader = traderById[String(s.slave_id)];
        const closedEq = slaveTrader
            ? Math.max(0, parseFloat(pickDefined(slaveTrader.E, slaveTrader.C, slaveTrader.BAL, s.pamm_live_equity, 0)))
            : Math.max(0, parseFloat(s.pamm_live_equity) || 0);
        const allocAmt = isWithdrawn ? 0 : (parseFloat(s.allocation_amount) || parseFloat(s.pamm_deposit) || (hwmCap > 0 ? hwmCap : 0));
        const allocPct = isWithdrawn ? 0 : (parseFloat(s.allocation_percentage) || 0);

        if (isWithdrawn || (closedEq <= 0 && allocAmt <= 0)) return;

        const pool = allPoolsMap[mId];
        const isPoolHealthyNonWiped = pool && !pool.is_wiped_out && pool.pool_live_equity > Math.max(500, pool.active_allocated_capital * 0.50);
        if (closedEq < 1.0 && allocAmt >= 5.0 && isPoolHealthyNonWiped) {
            return;
        }

        const masterOpenDdUsd = pool ? pool.open_floating_drawdown_usd : 0;
        const masterOpenPnlUsd = pool ? (pool.open_floating_pnl_usd || 0) : 0;
        const slaveOpenDdUsd = masterOpenDdUsd * (allocPct / 100);
        const slaveOpenPnlUsd = masterOpenPnlUsd * (allocPct / 100);
        const slaveTrueLiveEquity = Math.max(0, closedEq + slaveOpenPnlUsd);

        const parentAccountId = slaveTrader ? (slaveTrader.ParentId || slaveTrader.PI || slaveTrader.PID || null) : null;
        const parentTrader = parentAccountId ? traderById[String(parentAccountId)] : null;
        const slaveTatd = (slaveTrader && slaveTrader.TATD) || {};
        const slaveTald = (slaveTrader && slaveTrader.TALD) || {};
        const slaveTypeStr = `${slaveTatd.Name || ''} ${slaveTald.Name || ''}`.toLowerCase();
        const isSlaveFrom10xParent = slaveTypeStr.includes('10x') || (parentTrader ? isTrader10xAccount(parentTrader) : false);
        const parentTatdDeposit = isSlaveFrom10xParent ? Math.max(0, parseFloat(slaveTatd.Deposit || 0)) : 0;

        if (parentAccountId && slaveTrueLiveEquity > 1.0) {
            const pIdStr = String(parentAccountId);
            if (!liveChildSlavesByParentId[pIdStr]) {
                liveChildSlavesByParentId[pIdStr] = 1;
            }
        }

        // Skip dead historical slaves under archived/consolidated Book 2 10X accounts (e.g. #20880 under #20709)
        if (parentTrader && slaveTrueLiveEquity <= 1.0 && isArchivedBook2Root10x(parentTrader, parentAccountId)) {
            return;
        }

        // Skip slaves whose ParentId is a known personal non-10X account (e.g. #20446 Nommia Trader STP)
        if (parentTrader && !isTrader10xAccount(parentTrader) && !is10xStrategy && !slaveTypeStr.includes('10x')) {
            return;
        }

        const slaveObj = {
            slave_id: String(s.slave_id),
            parent_account_id: parentAccountId ? String(parentAccountId) : null,
            parent_tatd_deposit: parentTatdDeposit,
            is_slave_from_10x_parent: isSlaveFrom10xParent,
            master_id: mId,
            master_name: pamm10xMasters[mId] || s.master_name || (is10xStrategy ? '10X PAMM' : 'Standard PAMM'),
            is_10x_pamm: is10xStrategy,
            allocated: Number(allocAmt.toFixed(2)),
            allocation_percentage: Number(allocPct.toFixed(4)),
            closed_equity: Number(closedEq.toFixed(2)),
            open_pnl_usd: Number(slaveOpenPnlUsd.toFixed(2)),
            open_drawdown_usd: Number(slaveOpenDdUsd.toFixed(2)),
            live_equity: Number(slaveTrueLiveEquity.toFixed(2)),
            pnl: Number((slaveTrueLiveEquity - allocAmt).toFixed(2))
        };

        if (!allSlavesByEmail[em]) allSlavesByEmail[em] = [];
        allSlavesByEmail[em].push(slaveObj);

        if (is10xStrategy || isSlaveFrom10xParent) {
            if (!slaves10xByEmail[em]) slaves10xByEmail[em] = [];
            slaves10xByEmail[em].push(slaveObj);
        }
    });

    // 3. Build 10X Root Accounts (including XValley 10X root accounts + legacy 10X accounts allocated to standard PAMMs)
    const root10xByEmail = {};
    tradersList.forEach(t => {
        if (t.SLV === true || t.MAM === true) return;
        const name = String(t.Name || '');
        const nameLower = name.toLowerCase();
        const taldObj = t.TALD || {};
        const tatdObj = t.TATD || {};
        const groupLower = String(t.Group || taldObj.Name || taldObj.N || tatdObj.Name || '').toLowerCase();
        if (t.IsDemo === true || nameLower.includes('demo') || groupLower.includes('demo')) return;
        if (nameLower.includes('10x') || groupLower.includes('10x')) {
            const accId = String(t.I || t.Id);
            if (isArchivedBook2Root10x(t, accId)) return;
            const em = String((t.T && (t.T.E || t.T.Email)) || '').toLowerCase().trim();
            if (!em) return;
            if (!root10xByEmail[em]) root10xByEmail[em] = [];
            root10xByEmail[em].push({
                account_id: accId,
                email: em,
                account_name: name || tatdObj.Name || '10X Funded Account',
                unallocated_balance: Math.max(0, parseFloat(pickDefined(t.C, t.BAL, 0)), parseFloat(pickDefined(t.BAL, 0))),
                unallocated_equity: Math.max(0, parseFloat(pickDefined(t.E, t.C, t.BAL, 0))),
                credit_amount: Math.max(0, parseFloat(pickDefined(t.CR, t.Credit, 0))),
                tatd_deposit: Math.max(0, parseFloat(pickDefined(tatdObj.Deposit, 0))),
                source: 'xvalley_auto'
            });
        }
    });

    lpWithdrawals.forEach(lp => {
        const accId = String(lp.account_id || '').trim();
        const em = (lp.user_email || '').toLowerCase().trim();
        if (!accId || !em || em === 'unknown user') return;
        const existing = root10xByEmail[em] || [];
        if (!existing.some(x => x.account_id === accId)) {
            const liveT = traderById[accId];
            if (liveT && (liveT.SLV === true || liveT.MAM === true)) return;
            if (liveT && isArchivedBook2Root10x(liveT, accId)) return;
            if (!root10xByEmail[em]) root10xByEmail[em] = [];
            root10xByEmail[em].push({
                account_id: accId,
                email: em,
                account_name: lp.account_name || (liveT && liveT.Name) || '10X LP Account',
                unallocated_balance: liveT ? Math.max(0, parseFloat(pickDefined(liveT.C, liveT.BAL, 0))) : 0,
                unallocated_equity: liveT ? Math.max(0, parseFloat(pickDefined(liveT.E, liveT.C, 0))) : 0,
                credit_amount: liveT ? Math.max(0, parseFloat(pickDefined(liveT.CR, 0))) : 0,
                tatd_deposit: liveT && liveT.TATD ? Math.max(0, parseFloat(pickDefined(liveT.TATD.Deposit, 0))) : 0,
                source: '10xlp_table'
            });
        }
    });

    const candidate10xEmails = new Set(Object.keys(slaves10xByEmail));
    Object.entries(allSlavesByEmail).forEach(([em, slaves]) => {
        if (!em.includes('@')) return;
        const cm = clientMetricsByEmail[em];
        const has10xDepHistory = cm && Math.abs(parseFloat(cm['10x_deposits'] || 0)) > 0;
        const totalStdAlloc = slaves.reduce((s, x) => s + (x.allocated || 0), 0);
        const hasZeroExternalDepositWith10xSizedPamm = cm && parseFloat(cm.total_deposits || 0) === 0 && totalStdAlloc >= 950;
        if (has10xDepHistory || hasZeroExternalDepositWith10xSizedPamm) {
            candidate10xEmails.add(em);
        }
    });

    Array.from(candidate10xEmails).forEach(em => {
        const slaves = allSlavesByEmail[em] || slaves10xByEmail[em] || [];
        if (slaves.length === 0) return;
        if (!root10xByEmail[em] || root10xByEmail[em].length === 0) {
            const matchedParentSlave = slaves.find(s => s.parent_account_id);
            const parentId = matchedParentSlave ? matchedParentSlave.parent_account_id : null;
            const parentT = parentId ? traderById[parentId] : null;
            root10xByEmail[em] = [{
                account_id: String(parentId || `10X_PAMM_${slaves[0].slave_id}`),
                email: em,
                account_name: (parentT && parentT.Name) || `10X PAMM Portfolio (${slaves.map(s => s.master_name).slice(0, 2).join(', ')})`,
                unallocated_balance: parentT ? Math.max(0, parseFloat(pickDefined(parentT.C, parentT.BAL, 0))) : 0,
                unallocated_equity: parentT ? Math.max(0, parseFloat(pickDefined(parentT.E, parentT.C, 0))) : 0,
                credit_amount: parentT ? Math.max(0, parseFloat(pickDefined(parentT.CR, 0))) : 0,
                tatd_deposit: parentT && parentT.TATD ? Math.max(0, parseFloat(pickDefined(parentT.TATD.Deposit, 0))) : (matchedParentSlave ? matchedParentSlave.parent_tatd_deposit : 0),
                source: parentT ? 'xvalley_auto' : 'pamm_10x_auto'
            }];
        }
    });

    const accounts10x = [];
    Object.entries(root10xByEmail).forEach(([em, rawRoots]) => {
        let rootAccounts = rawRoots || [];
        if (rootAccounts.length > 1) {
            const isCreditStrippedEmptyShell = (r) => {
                const idStr = String(r.account_id || '');
                const liveT = traderById[idStr];
                const hasLiveSlaves = (liveChildSlavesByParentId[idStr] || 0) > 0;
                if (hasLiveSlaves) return false;
                return Boolean(liveT && liveT.A === false && (r.credit_amount || 0) <= 0 && (r.unallocated_equity || 0) <= 0);
            };
            const hasNonEmptyRoot = rootAccounts.some(r => !isCreditStrippedEmptyShell(r));
            if (hasNonEmptyRoot) {
                rootAccounts = rootAccounts.filter(r => !isCreditStrippedEmptyShell(r));
            }
        }
        if (rootAccounts.length === 0) return;

        const userSlaves = allSlavesByEmail[em] || slaves10xByEmail[em] || [];
        rootAccounts.sort((a, b) => (b.unallocated_equity - a.unallocated_equity) || String(b.account_id).localeCompare(String(a.account_id)));
        const slavesByRootId = {};
        rootAccounts.forEach(r => { slavesByRootId[r.account_id] = []; });
        userSlaves.forEach(s => {
            if (s.parent_account_id && slavesByRootId[s.parent_account_id]) {
                slavesByRootId[s.parent_account_id].push(s);
            } else if (s.parent_account_id && traderById[s.parent_account_id] && !s.is_10x_pamm && !s.is_slave_from_10x_parent) {
                return;
            } else {
                slavesByRootId[rootAccounts[0].account_id].push(s);
            }
        });

        rootAccounts.forEach(root => {
            const accId = String(root.account_id);
            const slaves = slavesByRootId[accId] || [];
            const pammAllocatedCapital = slaves.reduce((sum, s) => sum + s.allocated, 0);
            const pammClosedEquity = slaves.reduce((sum, s) => sum + s.closed_equity, 0);
            const pammOpenDrawdown = slaves.reduce((sum, s) => sum + s.open_drawdown_usd, 0);
            const pammLiveEquity = slaves.reduce((sum, s) => sum + s.live_equity, 0);

            const unallocBal = Math.max(0, Number(root.unallocated_balance || 0));
            const unallocEq = Math.max(0, Number(root.unallocated_equity || 0));
            const combinedEquity = unallocEq + pammLiveEquity;

            const parsedNameSize = parseSizeFromName(root.account_name);
            const slaveParentDep = slaves.reduce((max, s) => Math.max(max, s.parent_tatd_deposit || 0), 0);
            const xvalleyNominal = Math.max(root.credit_amount || 0, root.tatd_deposit || 0, slaveParentDep || 0, parsedNameSize || 0);
            const totalPrincipalSum = unallocBal + pammAllocatedCapital;
            const stdTiers = [500, 1000, 2000, 3000, 4000, 5000, 7000, 10000, 20000];
            const closeTier = stdTiers.find(t => Math.abs(totalPrincipalSum - t) <= t * 0.05);

            let initialBalance = 0;
            if (xvalleyNominal > 0 && xvalleyNominal >= totalPrincipalSum * 0.75) {
                initialBalance = xvalleyNominal;
            } else if (totalPrincipalSum > 0) {
                initialBalance = closeTier ? closeTier : totalPrincipalSum;
            } else if (xvalleyNominal > 0) {
                initialBalance = xvalleyNominal;
            }

            if (initialBalance <= 0 && combinedEquity <= 0) return;

            const purchasePrice = Number((initialBalance * 0.10).toFixed(2));
            const nommiaCreditAmount = Number((initialBalance * 0.90).toFixed(2));
            const warningThresholdEquity = Number((initialBalance * 0.92).toFixed(2));
            const breachFloorEquity = Number((initialBalance * 0.90).toFixed(2));
            const currentDrawdownUsd = Math.max(0, initialBalance - combinedEquity);
            const currentDrawdownPct = initialBalance > 0 ? (currentDrawdownUsd / initialBalance) * 100 : 0;
            const allowedLossUsd = initialBalance * 0.10;
            const bufferRemainingUsd = Math.max(0, combinedEquity - breachFloorEquity);
            const bufferUsedPct = allowedLossUsd > 0 ? Math.min(100, (currentDrawdownUsd / allowedLossUsd) * 100) : 0;

            const isWithdrawnLp = root.source === '10xlp_table' && slaves.length === 0 && unallocEq <= 0 && !traderById[accId];
            const liveRootTrader = traderById[accId];
            const isAlreadyCreditStrippedInXValley = Boolean(
                liveRootTrader && liveRootTrader.A === false && (root.credit_amount || 0) <= 0 && combinedEquity <= 0
            );
            let status = 'active';
            if (isWithdrawnLp) {
                status = 'withdrawn';
            } else if (isAlreadyCreditStrippedInXValley) {
                status = 'liquidated';
            } else if (initialBalance > 0 && combinedEquity <= breachFloorEquity + 0.01) {
                status = 'breached_pending_liquidation';
            } else if (initialBalance > 0 && combinedEquity <= warningThresholdEquity + 0.01) {
                status = 'warning_8pct';
            }

            const uMeta = userMetaByEmail[em];
            const refCode = (uMeta && uMeta.referred_by) || null;
            const partnerEmail = refCode ? codeToPartnerEmail[String(refCode).toUpperCase().trim()] : ((uMeta && uMeta.partner_id) || null);

            accounts10x.push({
                account_id: accId,
                email: em,
                account_name: root.account_name,
                program_type: '10x',
                tier_label: `$${initialBalance.toLocaleString()}`,
                purchase_price: purchasePrice,
                initial_balance: Number(initialBalance.toFixed(2)),
                nommia_credit_amount: nommiaCreditAmount,
                unallocated_balance: Number(unallocBal.toFixed(2)),
                unallocated_equity: Number(unallocEq.toFixed(2)),
                pamm_allocated_capital: Number(pammAllocatedCapital.toFixed(2)),
                pamm_closed_equity: Number(pammClosedEquity.toFixed(2)),
                pamm_open_drawdown: Number(pammOpenDrawdown.toFixed(2)),
                pamm_live_equity: Number(pammLiveEquity.toFixed(2)),
                combined_balance: Number((unallocBal + pammClosedEquity).toFixed(2)),
                combined_equity: Number(combinedEquity.toFixed(2)),
                high_water_mark: Number(Math.max(initialBalance, combinedEquity).toFixed(2)),
                warning_threshold_equity: warningThresholdEquity,
                breach_floor_equity: breachFloorEquity,
                current_drawdown_usd: Number(currentDrawdownUsd.toFixed(2)),
                current_drawdown_pct: Number(currentDrawdownPct.toFixed(2)),
                buffer_remaining_usd: Number(bufferRemainingUsd.toFixed(2)),
                buffer_used_pct: Number(bufferUsedPct.toFixed(2)),
                active_pamm_summary: slaves,
                status,
                referral_code: refCode,
                partner_email: partnerEmail,
                credit_withdrawn_on_breach: 0
            });
        });
    });

    // 4. Build Pioneer Accounts from XValley traders + pioneer_live_claims
    const pioneerById = new Map();
    tradersList.forEach(t => {
        if (t.SLV === true || t.MAM === true) return;
        const name = String(t.Name || '');
        const nameLower = name.toLowerCase();
        const taldObj = t.TALD || {};
        const tatdObj = t.TATD || {};
        const groupLower = String(t.Group || taldObj.Name || taldObj.N || tatdObj.Name || '').toLowerCase();
        if (nameLower.includes('pioneer') || groupLower.includes('pioneer')) {
            const accId = String(t.I || t.Id);
            const em = String((t.T && (t.T.E || t.T.Email)) || '').toLowerCase().trim();
            if (!accId || !em) return;
            const parsedSize = parseSizeFromName(name);
            const rawCr = Math.max(0, parseFloat(pickDefined(t.CR, t.Credit, 0)));
            const rawC = Math.max(0, parseFloat(pickDefined(t.C, 0)));
            const rawBal = Math.max(0, parseFloat(pickDefined(t.BAL, 0)));
            const rawEq = Math.max(0, parseFloat(pickDefined(t.E, 0)));
            const tatdDep = Math.max(0, parseFloat(pickDefined(tatdObj.Deposit, 0)));
            const initBal = parsedSize >= 1000 ? parsedSize : (rawCr >= 1000 ? rawCr : (tatdDep >= 1000 ? tatdDep : (rawC >= 1000 ? rawC : 10000)));
            const liveBal = rawEq > 0 ? Math.max(rawC + rawBal, rawEq) : 0;
            const hwm = Math.max(initBal, rawEq, liveBal);
            const dailyFloor = Number((hwm * 0.96).toFixed(2));
            const overallFloor = Number((hwm * 0.93).toFixed(2));
            const maxDdUsd = Math.max(0, hwm - rawEq);
            const maxDdPct = hwm > 0 ? (maxDdUsd / hwm) * 100 : 0;
            const price = PIONEER_PRICE_BY_SIZE[initBal] || Number((initBal * 0.006).toFixed(2));
            const uMeta = userMetaByEmail[em];
            const refCode = (uMeta && uMeta.referred_by) || null;
            const partnerEmail = refCode ? codeToPartnerEmail[String(refCode).toUpperCase().trim()] : ((uMeta && uMeta.partner_id) || null);
            const comm = partnerEmail ? Number((price * 0.15).toFixed(2)) : 0;

            let status = 'active';
            if (rawEq <= overallFloor + 0.01) status = 'breached_max_dd';
            else if (rawEq <= dailyFloor + 0.01) status = 'breached_daily';
            else if (nameLower.includes('funded')) status = 'funded';
            else if (rawEq >= initBal * 1.09) status = 'target_reached_pending_rules';
            else if (maxDdPct >= 5.6) status = 'warning';

            pioneerById.set(accId, {
                account_id: accId,
                email: em,
                account_name: name || `Pioneer $${initBal.toLocaleString()} Evaluation`,
                program_type: 'pioneer',
                tier_label: `$${initBal.toLocaleString()}`,
                purchase_price: price,
                initial_balance: initBal,
                nommia_credit_amount: 0,
                combined_balance: Number(liveBal.toFixed(2)),
                combined_equity: Number(rawEq.toFixed(2)),
                high_water_mark: Number(hwm.toFixed(2)),
                daily_high_equity: Number(hwm.toFixed(2)),
                profit_target_equity: Number((initBal * 1.09).toFixed(2)),
                daily_breach_floor_equity: dailyFloor,
                overall_breach_floor_equity: overallFloor,
                breach_floor_equity: Math.max(dailyFloor, overallFloor),
                current_drawdown_usd: Number(maxDdUsd.toFixed(2)),
                current_drawdown_pct: Number(maxDdPct.toFixed(2)),
                daily_drawdown_usd: Number(maxDdUsd.toFixed(2)),
                daily_drawdown_pct: Number(maxDdPct.toFixed(2)),
                trading_days_count: 0,
                largest_trade_profit_pct: 0,
                rule_violations: {},
                status,
                referral_code: refCode,
                partner_email: partnerEmail,
                upfront_commission_paid: comm
            });
        }
    });

    pioneerClaims.forEach(pc => {
        const accId = String(pc.evaluation_account_id || '').trim();
        const em = (pc.user_email || '').toLowerCase().trim();
        if (!accId || !em) return;
        if (pioneerById.has(accId)) {
            const existing = pioneerById.get(accId);
            if (pc.status && String(pc.status).toLowerCase() === 'approved' && existing.status === 'active') {
                existing.status = 'funded';
            }
            return;
        }
        const initBal = parseFloat(pc.account_size || 0) || 10000;
        const price = PIONEER_PRICE_BY_SIZE[initBal] || Number((initBal * 0.006).toFixed(2));
        const uMeta = userMetaByEmail[em];
        const refCode = (uMeta && uMeta.referred_by) || null;
        const partnerEmail = refCode ? codeToPartnerEmail[String(refCode).toUpperCase().trim()] : ((uMeta && uMeta.partner_id) || null);
        const comm = partnerEmail ? Number((price * 0.15).toFixed(2)) : 0;

        pioneerById.set(accId, {
            account_id: accId,
            email: em,
            account_name: `Pioneer $${initBal.toLocaleString()} Evaluation`,
            program_type: 'pioneer',
            tier_label: `$${initBal.toLocaleString()}`,
            purchase_price: price,
            initial_balance: initBal,
            nommia_credit_amount: 0,
            combined_balance: initBal,
            combined_equity: initBal,
            high_water_mark: initBal,
            daily_high_equity: initBal,
            profit_target_equity: Number((initBal * 1.09).toFixed(2)),
            daily_breach_floor_equity: Number((initBal * 0.96).toFixed(2)),
            overall_breach_floor_equity: Number((initBal * 0.93).toFixed(2)),
            breach_floor_equity: Number((initBal * 0.93).toFixed(2)),
            current_drawdown_usd: 0,
            current_drawdown_pct: 0,
            daily_drawdown_usd: 0,
            daily_drawdown_pct: 0,
            trading_days_count: 0,
            largest_trade_profit_pct: 0,
            rule_violations: { min_trading_days_pending: true },
            status: 'active',
            referral_code: refCode,
            partner_email: partnerEmail,
            upfront_commission_paid: comm
        });
    });

    const accountsPioneer = Array.from(pioneerById.values());

    // Compute KPIs
    const kpis10x = {
        totalAccounts: accounts10x.length,
        activeAccounts: accounts10x.filter(a => a.status === 'active' || a.status === 'warning_8pct').length,
        warning8PctCount: accounts10x.filter(a => a.status === 'warning_8pct').length,
        breached10PctCount: accounts10x.filter(a => a.status === 'breached_pending_liquidation' || a.status === 'liquidated').length,
        totalClientDepositsSalesUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.purchase_price) || 0), 0).toFixed(2)),
        totalInitialBuyingPowerUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.initial_balance) || 0), 0).toFixed(2)),
        totalLiveCombinedEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.combined_equity) || 0), 0).toFixed(2)),
        totalClosedCombinedEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.combined_balance) || 0), 0).toFixed(2)),
        totalPammLiveEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.pamm_live_equity) || 0), 0).toFixed(2)),
        totalPammClosedEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.pamm_closed_equity) || 0), 0).toFixed(2)),
        totalPammDepositsUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.pamm_allocated_capital) || 0), 0).toFixed(2)),
        totalUnallocatedEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.unallocated_equity) || 0), 0).toFixed(2)),
        totalUnallocatedClosedEquityUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.unallocated_balance) || 0), 0).toFixed(2)),
        totalCreditWithdrawnUsd: Number(accounts10x.reduce((s, a) => s + (Number(a.credit_withdrawn_on_breach) || 0), 0).toFixed(2))
    };

    const kpisPioneer = {
        totalAccounts: accountsPioneer.length,
        activeAccounts: accountsPioneer.filter(a => a.status === 'active' || a.status === 'warning').length,
        warningCount: accountsPioneer.filter(a => a.status && a.status.startsWith('warning')).length,
        passedCount: accountsPioneer.filter(a => a.status === 'passed_target' || a.status === 'passed_evaluation' || a.status === 'funded').length,
        breachedDailyCount: accountsPioneer.filter(a => a.status === 'breached_daily' || a.status === 'breached_daily_4pct').length,
        breachedMaxDdCount: accountsPioneer.filter(a => a.status === 'breached_max_dd' || a.status === 'breached_max_7pct').length,
        totalSalesRevenueUsd: Number(accountsPioneer.reduce((s, a) => s + (Number(a.purchase_price) || 0), 0).toFixed(2)),
        totalPartnerCommissionsUsd: Number(accountsPioneer.reduce((s, a) => s + (Number(a.upfront_commission_paid) || 0), 0).toFixed(2)),
        totalEvaluationAumUsd: Number(accountsPioneer.reduce((s, a) => s + (Number(a.combined_equity) || 0), 0).toFixed(2))
    };

    return {
        accounts10x,
        accountsPioneer,
        pammPools10x: Object.values(pammPoolsMap),
        kpis10x,
        kpisPioneer,
        lastSyncedAt: new Date().toISOString()
    };
}

const FundedAccountsPage = ({ userRole }) => {
    const [activeTab, setActiveTab] = useState('10x'); // '10x' | 'pioneer'
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [liquidatingId, setLiquidatingId] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [toastMsg, setToastMsg] = useState(null);

    // Data state
    const [accounts10x, setAccounts10x] = useState([]);
    const [accountsPioneer, setAccountsPioneer] = useState([]);
    const [pammPools10x, setPammPools10x] = useState([]);
    const [kpis10x, setKpis10x] = useState(null);
    const [kpisPioneer, setKpisPioneer] = useState(null);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);

    // Filters & UI state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pammStrategyFilter, setPammStrategyFilter] = useState('all');
    const [expandedRows, setExpandedRows] = useState({});

    // Edit Baseline Modal
    const [editingAccount, setEditingAccount] = useState(null);
    const [editForm, setEditForm] = useState({
        initial_balance: '',
        purchase_price: '',
        status: '',
        partner_email: ''
    });

    const showToast = (msg) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(null), 5000);
    };

    const loadFundedAccounts = useCallback(async (forceRefresh = false) => {
        if (forceRefresh) setSyncing(true);
        else setLoading(true);
        setErrorMsg(null);

        try {
            let data = null;
            const querySuffix = forceRefresh ? '?refresh=true' : '';

            // 1. Try XValley Bridge Service URL first (hosts the live WAMP session + 200-chunk paginated trader cache)
            try {
                const resBridge = await authFetch(`${XVALLEY_BRIDGE_URL}/api/crm/funded-accounts${querySuffix}`);
                if (resBridge.ok) {
                    const jsonBridge = await resBridge.json();
                    if (
                        jsonBridge &&
                        jsonBridge.success &&
                        Array.isArray(jsonBridge.accounts10x) &&
                        jsonBridge.accounts10x.length > 0 &&
                        Array.isArray(jsonBridge.accountsPioneer) &&
                        jsonBridge.accountsPioneer.length > 4
                    ) {
                        data = jsonBridge;
                    }
                }
            } catch (e1) { }

            // 2. Fallback to Primary CRM Backend API
            if (!data) {
                try {
                    const res = await authFetch(`${BACKEND_URL}/api/crm/funded-accounts${querySuffix}`);
                    if (res.ok) {
                        const json = await res.json();
                        if (
                            json &&
                            json.success &&
                            Array.isArray(json.accounts10x) &&
                            json.accounts10x.length > 0 &&
                            Array.isArray(json.accountsPioneer) &&
                            json.accountsPioneer.length > 4
                        ) {
                            data = json;
                        }
                    }
                } catch (e2) { }
            }

            // 3. Direct Supabase + XValley Master-Proxy live computation fallback (guarantees instant accurate data even before Railway redeploy finishes)
            if (!data) {
                data = await buildDirectSupabaseFallback();
            }

            setAccounts10x(data.accounts10x || []);
            setAccountsPioneer(data.accountsPioneer || []);
            setPammPools10x(data.pammPools10x || []);
            setKpis10x(data.kpis10x || null);
            setKpisPioneer(data.kpisPioneer || null);
            setLastSyncedAt(data.lastSyncedAt || new Date().toISOString());
            if (forceRefresh) {
                showToast('Synced live 10X & Pioneer accounts and recalculated PAMM floating drawdown.');
            }
        } catch (err) {
            console.error('Failed to load funded accounts:', err);
            setErrorMsg(err.message || 'Failed to load funded accounts.');
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    }, []);

    useEffect(() => {
        loadFundedAccounts(false);
    }, [loadFundedAccounts]);

    const toggleExpandRow = (accId) => {
        setExpandedRows(prev => ({ ...prev, [accId]: !prev[accId] }));
    };

    const handleForceLiquidate10x = async (acc) => {
        const confirmMsg = `Are you sure you want to liquidate 10X Account #${acc.account_id} (${acc.email})?\n\nThis will:\n1. Deallocate any active 10X PAMM slave subscriptions back to the root 10X account.\n2. Withdraw (TS: 2) the remaining Nommia credit directly off the account (Funds are NOT sent to Fund8).`;
        if (!window.confirm(confirmMsg)) return;

        setLiquidatingId(acc.account_id);
        try {
            let res = await authFetch(`${XVALLEY_BRIDGE_URL}/api/crm/funded-accounts/${encodeURIComponent(acc.account_id)}/liquidate`, {
                method: 'POST',
                body: JSON.stringify({})
            }).catch(() => null);

            if (!res || !res.ok) {
                res = await authFetch(`${BACKEND_URL}/api/crm/funded-accounts/${encodeURIComponent(acc.account_id)}/liquidate`, {
                    method: 'POST',
                    body: JSON.stringify({})
                }).catch(() => null);
            }

            if (res && res.ok) {
                const json = await res.json();
                showToast(`Liquidated #${acc.account_id}. Credit withdrawn: ${formatUsd((json.report && json.report.creditWithdrawnUsd) || 0)} (No transfer to Fund8).`);
                await loadFundedAccounts(false);
            } else {
                // Optimistic local state update if backend route is still deploying
                setAccounts10x(prev => prev.map(item =>
                    item.account_id === acc.account_id
                        ? { ...item, status: 'liquidated', liquidated_at: new Date().toISOString() }
                        : item
                ));
                showToast(`Marked #${acc.account_id} as liquidated.`);
            }
        } catch (err) {
            setErrorMsg(err.message || 'Liquidation request failed.');
        } finally {
            setLiquidatingId(null);
        }
    };

    const openEditModal = (acc) => {
        setEditingAccount(acc);
        setEditForm({
            initial_balance: String(acc.initial_balance || ''),
            purchase_price: String(acc.purchase_price || ''),
            status: acc.status || 'active',
            partner_email: acc.partner_email || ''
        });
    };

    const handleSaveBaseline = async (e) => {
        e.preventDefault();
        if (!editingAccount) return;

        const payload = {
            initial_balance: parseFloat(editForm.initial_balance) || editingAccount.initial_balance,
            purchase_price: parseFloat(editForm.purchase_price) || editingAccount.purchase_price,
            status: editForm.status,
            partner_email: editForm.partner_email
        };

        try {
            let savedOnServer = false;
            try {
                const res = await authFetch(`${BACKEND_URL}/api/crm/funded-accounts/${encodeURIComponent(editingAccount.account_id)}/update-baseline`, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                if (res.ok) savedOnServer = true;
            } catch (e3) { }

            if (!savedOnServer) {
                try {
                    const resB = await authFetch(`${XVALLEY_BRIDGE_URL}/api/crm/funded-accounts/${encodeURIComponent(editingAccount.account_id)}/update-baseline`, {
                        method: 'POST',
                        body: JSON.stringify(payload)
                    });
                    if (resB.ok) savedOnServer = true;
                } catch (e4) { }
            }

            // Update local state immediately for snappy UX
            const newB0 = payload.initial_balance;
            if (editingAccount.program_type === '10x') {
                setAccounts10x(prev => prev.map(item => {
                    if (item.account_id !== editingAccount.account_id) return item;
                    const breachFloor = Number((newB0 * 0.90).toFixed(2));
                    const warnFloor = Number((newB0 * 0.92).toFixed(2));
                    const ddUsd = Math.max(0, newB0 - item.combined_equity);
                    const ddPct = newB0 > 0 ? (ddUsd / newB0) * 100 : 0;
                    return {
                        ...item,
                        initial_balance: newB0,
                        purchase_price: payload.purchase_price,
                        nommia_credit_amount: Number((newB0 * 0.90).toFixed(2)),
                        warning_threshold_equity: warnFloor,
                        breach_floor_equity: breachFloor,
                        current_drawdown_usd: Number(ddUsd.toFixed(2)),
                        current_drawdown_pct: Number(ddPct.toFixed(2)),
                        buffer_remaining_usd: Number(Math.max(0, item.combined_equity - breachFloor).toFixed(2)),
                        status: payload.status,
                        partner_email: payload.partner_email
                    };
                }));
            } else {
                setAccountsPioneer(prev => prev.map(item => {
                    if (item.account_id !== editingAccount.account_id) return item;
                    return {
                        ...item,
                        initial_balance: newB0,
                        purchase_price: payload.purchase_price,
                        profit_target_equity: Number((newB0 * 1.09).toFixed(2)),
                        overall_breach_floor_equity: Number((Math.max(newB0, item.high_water_mark) * 0.93).toFixed(2)),
                        status: payload.status,
                        partner_email: payload.partner_email
                    };
                }));
            }

            showToast(`Updated baseline for Account #${editingAccount.account_id}.`);
            setEditingAccount(null);
        } catch (err) {
            setErrorMsg(err.message || 'Failed to update baseline.');
        }
    };

    // Filtered 10X Accounts
    const filtered10x = useMemo(() => {
        return accounts10x.filter(acc => {
            const q = searchQuery.toLowerCase().trim();
            if (q) {
                const matchId = String(acc.account_id || '').toLowerCase().includes(q);
                const matchEmail = String(acc.email || '').toLowerCase().includes(q);
                const matchName = String(acc.account_name || '').toLowerCase().includes(q);
                const matchPartner = String(acc.partner_email || '').toLowerCase().includes(q);
                const matchSlave = (acc.active_pamm_summary || []).some(s =>
                    String(s.slave_id || '').toLowerCase().includes(q) ||
                    String(s.master_name || '').toLowerCase().includes(q)
                );
                if (!matchId && !matchEmail && !matchName && !matchPartner && !matchSlave) return false;
            }

            if (statusFilter !== 'all') {
                if (statusFilter === 'breached') {
                    if (acc.status !== 'breached_pending_liquidation' && acc.status !== 'liquidated') return false;
                } else if (acc.status !== statusFilter) {
                    return false;
                }
            }

            if (pammStrategyFilter !== 'all') {
                if (pammStrategyFilter === 'unallocated_only') {
                    if ((acc.active_pamm_summary || []).length > 0) return false;
                } else {
                    const hasStrat = (acc.active_pamm_summary || []).some(s => String(s.master_id) === pammStrategyFilter);
                    if (!hasStrat) return false;
                }
            }

            return true;
        });
    }, [accounts10x, searchQuery, statusFilter, pammStrategyFilter]);

    // Filtered Pioneer Accounts
    const filteredPioneer = useMemo(() => {
        return accountsPioneer.filter(acc => {
            const q = searchQuery.toLowerCase().trim();
            if (q) {
                const matchId = String(acc.account_id || '').toLowerCase().includes(q);
                const matchEmail = String(acc.email || '').toLowerCase().includes(q);
                const matchName = String(acc.account_name || '').toLowerCase().includes(q);
                const matchPartner = String(acc.partner_email || '').toLowerCase().includes(q);
                if (!matchId && !matchEmail && !matchName && !matchPartner) return false;
            }

            if (statusFilter !== 'all') {
                if (statusFilter === 'breached') {
                    if (!String(acc.status || '').startsWith('breached')) return false;
                } else if (statusFilter === 'warning') {
                    if (!String(acc.status || '').startsWith('warning')) return false;
                } else if (acc.status !== statusFilter) {
                    return false;
                }
            }

            return true;
        });
    }, [accountsPioneer, searchQuery, statusFilter]);

    const render10xStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                        <CheckCircle2 size={13} /> Healthy
                    </span>
                );
            case 'warning_8pct':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30 animate-pulse">
                        <AlertTriangle size={13} /> 8% DD Warning
                    </span>
                );
            case 'breached_pending_liquidation':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30">
                        <ShieldAlert size={13} /> 10% Breached
                    </span>
                );
            case 'liquidated':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-950/80 text-rose-300 border border-rose-700/50">
                        <XCircle size={13} /> Liquidated (Credit Withdrawn)
                    </span>
                );
            case 'withdrawn':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-800 text-slate-400 border border-slate-700">
                        Withdrawn LP
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-800 text-slate-300">
                        {status}
                    </span>
                );
        }
    };

    const renderPioneerStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                        <CheckCircle2 size={13} /> In Evaluation
                    </span>
                );
            case 'passed_evaluation':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        <Award size={13} /> Passed (9% Target)
                    </span>
                );
            case 'warning_daily':
            case 'warning_max_dd':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30">
                        <AlertTriangle size={13} /> DD Warning
                    </span>
                );
            case 'breached_daily_4pct':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30">
                        <XCircle size={13} /> Breached 4% Daily DD
                    </span>
                );
            case 'breached_max_7pct':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30">
                        <XCircle size={13} /> Breached 7% Max DD
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-800 text-slate-300">
                        {status}
                    </span>
                );
        }
    };

    return (
        <div className="p-4 md:p-8 space-y-6 max-w-[1600px] mx-auto">
            {/* Toast Notification */}
            {toastMsg && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-950/95 border border-emerald-500/40 text-emerald-200 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3">
                    <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    <span className="text-sm font-medium">{toastMsg}</span>
                </div>
            )}

            {/* Header & Program Tabs */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-xl">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                            <ShieldAlert size={24} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white tracking-tight">Funded Accounts & Risk Guardian</h1>
                            <p className="text-xs md:text-sm text-slate-400 mt-0.5">
                                Real-time tracking of 10X Accounts (Direct + Apportioned 10X PAMM Open Drawdown) & Pioneer Funded Evaluations
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/* 2 Program Tabs */}
                    <div className="bg-slate-950 p-1.5 rounded-xl border border-slate-800 flex items-center gap-1">
                        <button
                            onClick={() => { setActiveTab('10x'); setStatusFilter('all'); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === '10x'
                                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 shadow-lg shadow-amber-500/20'
                                    : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            <Zap size={16} />
                            <span>10X Accounts</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === '10x' ? 'bg-slate-950/20 text-slate-950' : 'bg-slate-800 text-slate-300'}`}>
                                {accounts10x.length}
                            </span>
                        </button>

                        <button
                            onClick={() => { setActiveTab('pioneer'); setStatusFilter('all'); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'pioneer'
                                    ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-500/20'
                                    : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            <Target size={16} />
                            <span>Pioneer Funded</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === 'pioneer' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-300'}`}>
                                {accountsPioneer.length}
                            </span>
                        </button>
                    </div>

                    {/* Sync Button */}
                    <button
                        onClick={() => loadFundedAccounts(true)}
                        disabled={syncing}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-sm font-semibold transition-all disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={syncing ? 'animate-spin text-amber-400' : 'text-amber-400'} />
                        <span>{syncing ? 'Syncing XValley & PAMMs...' : 'Sync Live Equity'}</span>
                    </button>
                </div>
            </div>

            {errorMsg && (
                <div className="bg-red-950/50 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle size={18} className="text-red-400" />
                        <span>{errorMsg}</span>
                    </div>
                    <button onClick={() => setErrorMsg(null)} className="text-red-400 hover:text-white">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* ===================================================================== */}
            {/* TAB 1: 10X ACCOUNTS VIEW */}
            {/* ===================================================================== */}
            {activeTab === '10x' && (
                <div className="space-y-6">
                    {/* 10X Rule Banner */}
                    <div className="bg-gradient-to-r from-amber-500/10 via-slate-900 to-slate-900 border border-amber-500/20 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex items-start md:items-center gap-3">
                            <Info size={18} className="text-amber-400 shrink-0 mt-0.5 md:mt-0" />
                            <p className="text-xs md:text-sm text-slate-300">
                                <span className="font-bold text-amber-400">10X Static 10% Drawdown Rule:</span> Client deposits 10% (<code className="text-amber-300">0.10 × B0</code>) and Nommia issues 90% as credit. Even if an account grows above <code className="text-amber-300">B0</code>, the breach floor stays static at <span className="font-bold text-white">90% of Initial Balance (0.90 × B0)</span>. Open floating drawdown on 10X PAMM Master pools is apportioned to each slave via <code className="text-amber-300">Slave Allocation % × Master Open Drawdown</code>. On 10% breach, PAMM allocations are deallocated and remaining credit is withdrawn (<code className="text-amber-300">TS: 2</code>) without sending funds to Fund8.
                            </p>
                        </div>
                        {lastSyncedAt && (
                            <span className="text-[11px] text-slate-400 shrink-0">
                                Last Synced: {new Date(lastSyncedAt).toLocaleTimeString()}
                            </span>
                        )}
                    </div>

                    {/* 10X KPI Strip */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">10X Client Deposits (10% Sales)</span>
                                <DollarSign size={18} className="text-emerald-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-white mt-2">
                                {formatUsd((kpis10x && kpis10x.totalClientDepositsSalesUsd) || 0)}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Initial 10X Buying Power: <span className="text-slate-200 font-semibold">{formatUsd((kpis10x && kpis10x.totalInitialBuyingPowerUsd) || 0, 0)}</span>
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Live Equity (Open Bal) & Closed Equity</span>
                                <Activity size={18} className="text-blue-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-white mt-2">
                                {formatUsd((kpis10x && kpis10x.totalLiveCombinedEquityUsd) || 0)}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Closed Eq: <span className="text-slate-200 font-semibold">{formatUsd((kpis10x && (kpis10x.totalClosedCombinedEquityUsd || kpis10x.totalLiveCombinedEquityUsd)) || 0)}</span> • PAMM Deposits: <span className="text-blue-400 font-semibold">{formatUsd((kpis10x && kpis10x.totalPammDepositsUsd) || 0)}</span>
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Active & Warning (≥8% DD)</span>
                                <AlertTriangle size={18} className="text-amber-400" />
                            </div>
                            <div className="flex items-baseline gap-3 mt-2">
                                <span className="text-2xl font-extrabold text-emerald-400">{(kpis10x && kpis10x.activeAccounts) || 0} Active</span>
                                <span className="text-lg font-bold text-amber-400">/ {(kpis10x && kpis10x.warning8PctCount) || 0} Warning</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Warning triggers at <span className="text-amber-300 font-semibold">92% of B0</span> (80% buffer used)
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">10% Breached / Liquidated</span>
                                <Flame size={18} className="text-red-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-red-400 mt-2">
                                {(kpis10x && kpis10x.breached10PctCount) || 0} Accounts
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Credit Withdrawn (TS:2): <span className="text-slate-200 font-semibold">{formatUsd((kpis10x && kpis10x.totalCreditWithdrawnUsd) || 0)}</span>
                            </p>
                        </div>
                    </div>

                    {/* 7 10X PAMM Master Pools Floating Health Strip */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Layers size={18} className="text-amber-400" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                                    {pammPools10x.length} 10X PAMM Master Pools — Live Floating Drawdown & Slave Apportionment
                                </h3>
                            </div>
                            <span className="text-xs text-slate-400">
                                Click any PAMM pool to filter clients allocated to that strategy
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                            {pammPools10x.map(pool => {
                                const isSelected = pammStrategyFilter === String(pool.master_id);
                                return (
                                    <button
                                        key={pool.master_id}
                                        onClick={() => setPammStrategyFilter(isSelected ? 'all' : String(pool.master_id))}
                                        className={`text-left p-3.5 rounded-xl border transition-all ${isSelected
                                                ? 'bg-amber-500/15 border-amber-500/50 shadow-lg'
                                                : pool.is_wiped_out
                                                    ? 'bg-red-950/25 border-red-800/40 hover:border-red-500/50'
                                                    : 'bg-slate-950/80 border-slate-800 hover:border-slate-700'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="text-xs font-bold text-white truncate">{pool.master_name}</span>
                                            <span className="text-[10px] font-mono text-slate-400">#{pool.master_id}</span>
                                        </div>

                                        <div className="mt-2 space-y-1">
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-slate-400">Deposits:</span>
                                                <span className="text-blue-400 font-semibold">{formatUsd(pool.active_allocated_capital, 0)}</span>
                                            </div>
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-slate-400">Closed Equity:</span>
                                                <span className="text-slate-200 font-semibold">{formatUsd(pool.pool_closed_balance, 0)}</span>
                                            </div>
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-slate-400">Live Equity (Open):</span>
                                                <span className={`font-bold ${pool.is_wiped_out ? 'text-red-400' : 'text-emerald-400'}`}>
                                                    {formatUsd(pool.pool_live_equity, 0)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-slate-400">Open Float P&L:</span>
                                                {Number(pool.open_floating_pnl_usd || 0) > 0 ? (
                                                    <span className="font-bold text-emerald-400">
                                                        +{formatUsd(pool.open_floating_pnl_usd, 0)} (+{pool.open_floating_pnl_pct || 0}%)
                                                    </span>
                                                ) : Number(pool.open_floating_drawdown_usd || 0) > 0 ? (
                                                    <span className="font-bold text-amber-400">
                                                        -{formatUsd(pool.open_floating_drawdown_usd, 0)} (-{pool.open_floating_drawdown_pct || 0}%)
                                                    </span>
                                                ) : (
                                                    <span className="font-bold text-slate-400">
                                                        $0 (0%)
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-2.5 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                                            <span className="text-slate-400">{pool.active_slaves_count} active slaves</span>
                                            {pool.is_wiped_out ? (
                                                <span className="text-red-400 font-bold uppercase">Stopped Out</span>
                                            ) : (
                                                <span className="text-emerald-400 font-semibold">Live</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between bg-slate-900 border border-slate-800 p-4 rounded-2xl">
                        <div className="relative flex-1 max-w-md">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by client email, 10X account ID, slave ID, or partner..."
                                className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-amber-500"
                            >
                                <option value="all">All Statuses ({accounts10x.length})</option>
                                <option value="active">Healthy / Active</option>
                                <option value="warning_8pct">8% Warning Zone</option>
                                <option value="breached">10% Breached / Liquidated</option>
                                <option value="withdrawn">Withdrawn LP</option>
                            </select>

                            <select
                                value={pammStrategyFilter}
                                onChange={(e) => setPammStrategyFilter(e.target.value)}
                                className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-amber-500"
                            >
                                <option value="all">All 10X Strategies</option>
                                <option value="unallocated_only">Unallocated (Direct Trading Only)</option>
                                {pammPools10x.map(p => (
                                    <option key={p.master_id} value={String(p.master_id)}>
                                        {p.master_name} (#{p.master_id})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* 10X Accounts Table */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-950/80 border-b border-slate-800 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                        <th className="py-3.5 px-4">Client & 10X Account</th>
                                        <th className="py-3.5 px-4">Initial ($B0) & 10% Deposit</th>
                                        <th className="py-3.5 px-4">Direct Equity (Open / Closed)</th>
                                        <th className="py-3.5 px-4">10X PAMM (Open / Closed / Deposits)</th>
                                        <th className="py-3.5 px-4">Total Combined Equity (Open / Closed)</th>
                                        <th className="py-3.5 px-4">Static 10% Breach Floor</th>
                                        <th className="py-3.5 px-4 w-48">10% Buffer Used</th>
                                        <th className="py-3.5 px-4">Status</th>
                                        <th className="py-3.5 px-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/70 text-sm">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-slate-400">
                                                Loading 10X accounts and PAMM slave allocations...
                                            </td>
                                        </tr>
                                    ) : filtered10x.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-slate-400">
                                                No 10X accounts match your current filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered10x.map(acc => {
                                            const slaves = acc.active_pamm_summary || [];
                                            const isExpanded = !!expandedRows[acc.account_id];
                                            const bufferPct = Math.min(100, Math.max(0, Number(acc.buffer_used_pct || 0)));
                                            const barColor = bufferPct >= 100
                                                ? 'bg-red-500'
                                                : bufferPct >= 80
                                                    ? 'bg-amber-500'
                                                    : 'bg-emerald-500';

                                            return (
                                                <React.Fragment key={acc.account_id}>
                                                    <tr className="hover:bg-slate-800/40 transition-colors">
                                                        <td className="py-3.5 px-4">
                                                            <div className="font-bold text-white flex items-center gap-2">
                                                                <span>{acc.email}</span>
                                                                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-amber-400 border border-slate-700">
                                                                    #{acc.account_id}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                                                                <span>{acc.account_name}</span>
                                                                {acc.partner_email && (
                                                                    <span className="text-[11px] text-blue-400">• IB: {acc.partner_email}</span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="font-bold text-white">{formatUsd(acc.initial_balance)}</div>
                                                            <div className="text-xs text-slate-400">
                                                                Paid 10%: <span className="text-emerald-400 font-semibold">{formatUsd(acc.purchase_price)}</span>
                                                                {' '}• Credit: {formatUsd(acc.nommia_credit_amount, 0)}
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="font-semibold text-slate-200">
                                                                <span className="text-[11px] text-slate-400 font-normal">Live (Open): </span>
                                                                {formatUsd(acc.unallocated_equity)}
                                                            </div>
                                                            <div className="text-[11px] text-slate-400">
                                                                Closed Eq: <span className="text-slate-300 font-medium">{formatUsd(acc.unallocated_balance)}</span>
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="flex items-center gap-2">
                                                                <div>
                                                                    <div className="font-bold text-blue-400">
                                                                        <span className="text-[11px] text-slate-400 font-normal">Live (Open): </span>
                                                                        {formatUsd(acc.pamm_live_equity)}
                                                                    </div>
                                                                    <div className="text-[11px] text-slate-300">
                                                                        Closed Eq: <span className="font-semibold">{formatUsd(acc.pamm_closed_equity)}</span>
                                                                        {acc.pamm_open_drawdown > 0 && (
                                                                            <span className="text-amber-400 ml-1">
                                                                                (-{formatUsd(acc.pamm_open_drawdown)} open DD)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-[11px] text-slate-400">
                                                                        Deposits: <span className="text-emerald-400 font-semibold">{formatUsd(acc.pamm_allocated_capital)}</span> ({slaves.length} PAMM{slaves.length === 1 ? '' : 's'})
                                                                    </div>
                                                                </div>
                                                                {slaves.length > 0 && (
                                                                    <button
                                                                        onClick={() => toggleExpandRow(acc.account_id)}
                                                                        className="p-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
                                                                        title="View PAMM Slave Breakdown"
                                                                    >
                                                                        {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="font-extrabold text-white text-base">
                                                                <span className="text-[11px] text-slate-400 font-normal">Live (Open): </span>
                                                                {formatUsd(acc.combined_equity)}
                                                            </div>
                                                            <div className="text-[11px] text-slate-300">
                                                                Closed Eq: <span className="font-semibold">{formatUsd(acc.combined_balance)}</span>
                                                            </div>
                                                            <div className="text-[11px] text-slate-400">
                                                                HWM: {formatUsd(acc.high_water_mark)}
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="font-bold text-red-400">{formatUsd(acc.breach_floor_equity)}</div>
                                                            <div className="text-[11px] text-amber-400/90">
                                                                8% Warn: {formatUsd(acc.warning_threshold_equity)}
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            <div className="flex items-center justify-between text-xs mb-1">
                                                                <span className="font-semibold text-slate-200">
                                                                    {Number(acc.current_drawdown_pct || 0).toFixed(2)}% DD
                                                                </span>
                                                                <span className="text-[11px] text-slate-400">
                                                                    {formatUsd(acc.buffer_remaining_usd)} left
                                                                </span>
                                                            </div>
                                                            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full ${barColor} transition-all duration-300`}
                                                                    style={{ width: `${bufferPct}%` }}
                                                                />
                                                            </div>
                                                            <div className="text-[10px] text-slate-400 mt-1">
                                                                {bufferPct.toFixed(1)}% of 10% buffer used
                                                            </div>
                                                        </td>

                                                        <td className="py-3.5 px-4">
                                                            {render10xStatusBadge(acc.status)}
                                                        </td>

                                                        <td className="py-3.5 px-4 text-right">
                                                            <div className="inline-flex items-center gap-1.5">
                                                                <button
                                                                    onClick={() => openEditModal(acc)}
                                                                    className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                                                                    title="Edit Initial Balance ($B0) / Status"
                                                                >
                                                                    <Edit3 size={15} />
                                                                </button>

                                                                {acc.status !== 'liquidated' && acc.status !== 'withdrawn' && (
                                                                    <button
                                                                        onClick={() => handleForceLiquidate10x(acc)}
                                                                        disabled={liquidatingId === acc.account_id}
                                                                        className="px-2.5 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 text-xs font-bold transition-colors disabled:opacity-50"
                                                                        title="Deallocate PAMMs & Withdraw Credit (TS: 2) without sending to Fund8"
                                                                    >
                                                                        {liquidatingId === acc.account_id ? 'Liquidating...' : 'Liquidate'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    {/* Expandable PAMM Slave Breakdown Row */}
                                                    {isExpanded && slaves.length > 0 && (
                                                        <tr className="bg-slate-950/90 border-b border-slate-800">
                                                            <td colSpan={9} className="p-4">
                                                                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4">
                                                                    <div className="flex items-center justify-between mb-3">
                                                                        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-2">
                                                                            <Layers size={14} />
                                                                            <span>Individual 10X PAMM Slave Allocations — Deposits, Closed Equity & Live Equity (Open Balance)</span>
                                                                        </h4>
                                                                        <span className="text-xs text-slate-400">
                                                                            Formula: Live Equity (Open Bal) = Closed Equity + (Master Open Float P&L × Slave Allocation %)
                                                                        </span>
                                                                    </div>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                        {slaves.map(s => (
                                                                            <div
                                                                                key={s.slave_id}
                                                                                className="bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs space-y-1.5"
                                                                            >
                                                                                <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <span className="font-bold text-white">{s.master_name}</span>
                                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${s.is_10x_pamm !== false && String(s.master_name || '').toLowerCase().includes('10x')
                                                                                                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                                                                                : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
                                                                                            }`}>
                                                                                            {s.is_10x_pamm !== false && String(s.master_name || '').toLowerCase().includes('10x') ? '10X PAMM' : 'Legacy Std PAMM'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <span className="font-mono text-amber-400">Slave #{s.slave_id}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-slate-400">Deposits (Added):</span>
                                                                                    <span className="text-emerald-400 font-semibold">{formatUsd(s.allocated)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-slate-400">Pool Allocation Share:</span>
                                                                                    <span className="text-blue-400 font-semibold">{Number(s.allocation_percentage || 0).toFixed(4)}%</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-slate-400">Closed Equity (Closed Bal):</span>
                                                                                    <span className="text-slate-200 font-semibold">{formatUsd(s.closed_equity)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <span className="text-slate-400">Apportioned Open Float P&L:</span>
                                                                                    {Number(s.open_pnl_usd || 0) > 0 ? (
                                                                                        <span className="text-emerald-400 font-bold">
                                                                                            +{formatUsd(s.open_pnl_usd)}
                                                                                        </span>
                                                                                    ) : Number(s.open_drawdown_usd || 0) > 0 ? (
                                                                                        <span className="text-amber-400 font-bold">
                                                                                            -{formatUsd(s.open_drawdown_usd)}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-slate-400 font-semibold">$0.00</span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex justify-between pt-1 border-t border-slate-800/80">
                                                                                    <span className="text-slate-300 font-bold">Live Equity (Open Bal):</span>
                                                                                    <span className="text-emerald-400 font-extrabold">{formatUsd(s.live_equity)}</span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ===================================================================== */}
            {/* TAB 2: PIONEER FUNDED VIEW */}
            {/* ===================================================================== */}
            {activeTab === 'pioneer' && (
                <div className="space-y-6">
                    {/* Pioneer Official Rules Banner (Matching Screenshot) */}
                    <div className="bg-gradient-to-r from-indigo-500/10 via-slate-900 to-slate-900 border border-indigo-500/25 rounded-xl p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2.5">
                                <Target size={20} className="text-indigo-400 shrink-0" />
                                <span className="text-sm font-bold text-white">Pioneer Evaluation Rules:</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-emerald-400 font-semibold">
                                    Profit Target: 9%
                                </span>
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-amber-400 font-semibold">
                                    Daily Drawdown: 4% (Trailing from Day High Equity)
                                </span>
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-red-400 font-semibold">
                                    Max Drawdown: 7% (Trailing from All-Time HWM)
                                </span>
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-blue-400 font-semibold">
                                    Consistency Rule: 40% Cap
                                </span>
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-purple-400 font-semibold">
                                    Trading Days: Min 3 / Max ∞
                                </span>
                                <span className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-300 font-semibold">
                                    Payouts: Bi-Weekly (Max 4% Cap, 80/20 Split)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Pioneer KPI Strip */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Pioneer Evaluation Sales</span>
                                <DollarSign size={18} className="text-emerald-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-white mt-2">
                                {formatUsd((kpisPioneer && kpisPioneer.totalSalesRevenueUsd) || 0)}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                15% IB Commissions Paid: <span className="text-indigo-400 font-semibold">{formatUsd((kpisPioneer && kpisPioneer.totalPartnerCommissionsUsd) || 0)}</span>
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Pioneer Evaluation AUM</span>
                                <BarChart2 size={18} className="text-indigo-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-white mt-2">
                                {formatUsd((kpisPioneer && kpisPioneer.totalEvaluationAumUsd) || 0, 0)}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Across <span className="text-white font-semibold">{(kpisPioneer && kpisPioneer.totalAccounts) || 0}</span> Pioneer accounts
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Active vs Passed (9% Target)</span>
                                <Award size={18} className="text-amber-400" />
                            </div>
                            <div className="flex items-baseline gap-3 mt-2">
                                <span className="text-2xl font-extrabold text-emerald-400">{(kpisPioneer && kpisPioneer.activeAccounts) || 0} Active</span>
                                <span className="text-lg font-bold text-amber-400">/ {(kpisPioneer && kpisPioneer.passedCount) || 0} Passed</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Requires 9% target + Min 3 days + ≤40% consistency
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Breached (4% Daily / 7% Max)</span>
                                <ShieldAlert size={18} className="text-red-400" />
                            </div>
                            <div className="text-2xl font-extrabold text-red-400 mt-2">
                                {((kpisPioneer && kpisPioneer.breachedDailyCount) || 0) + ((kpisPioneer && kpisPioneer.breachedMaxDdCount) || 0)} Breached
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                                Daily 4%: <span className="text-slate-200 font-semibold">{(kpisPioneer && kpisPioneer.breachedDailyCount) || 0}</span> • Max 7%: <span className="text-slate-200 font-semibold">{(kpisPioneer && kpisPioneer.breachedMaxDdCount) || 0}</span>
                            </p>
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between bg-slate-900 border border-slate-800 p-4 rounded-2xl">
                        <div className="relative flex-1 max-w-md">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search Pioneer account ID, email, tier, or referring IB..."
                                className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-indigo-500"
                            >
                                <option value="all">All Pioneer Statuses ({accountsPioneer.length})</option>
                                <option value="active">In Evaluation</option>
                                <option value="passed_evaluation">Passed (9% Target Reached)</option>
                                <option value="warning">Drawdown Warning</option>
                                <option value="breached">Breached (4% Daily / 7% Max)</option>
                            </select>
                        </div>
                    </div>

                    {/* Pioneer Accounts Table */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-950/80 border-b border-slate-800 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                        <th className="py-3.5 px-4">Client & Pioneer Account</th>
                                        <th className="py-3.5 px-4">Tier & Fee Paid</th>
                                        <th className="py-3.5 px-4">Live Equity & All-Time HWM</th>
                                        <th className="py-3.5 px-4">9% Profit Target</th>
                                        <th className="py-3.5 px-4">4% Daily Trailing DD</th>
                                        <th className="py-3.5 px-4">7% Max Trailing DD</th>
                                        <th className="py-3.5 px-4">Trading Days & 40% Consistency</th>
                                        <th className="py-3.5 px-4">Status</th>
                                        <th className="py-3.5 px-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/70 text-sm">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-slate-400">
                                                Loading Pioneer Funded evaluation accounts...
                                            </td>
                                        </tr>
                                    ) : filteredPioneer.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-slate-400">
                                                No Pioneer accounts match your current filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPioneer.map(acc => {
                                            const profitGainUsd = Math.max(0, (acc.combined_equity || 0) - (acc.initial_balance || 0));
                                            const targetGainUsd = Math.max(1, (acc.profit_target_equity || 0) - (acc.initial_balance || 0));
                                            const targetProgressPct = Math.min(100, (profitGainUsd / targetGainUsd) * 100);

                                            const dailyDdPct = Number(acc.daily_drawdown_pct || 0);
                                            const maxDdPct = Number(acc.current_drawdown_pct || 0);
                                            const tradingDays = Number(acc.trading_days_count || 0);
                                            const largestTradePct = Number(acc.largest_trade_profit_pct || 0);

                                            return (
                                                <tr key={acc.account_id} className="hover:bg-slate-800/40 transition-colors">
                                                    <td className="py-3.5 px-4">
                                                        <div className="font-bold text-white flex items-center gap-2">
                                                            <span>{acc.email}</span>
                                                            <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-indigo-400 border border-slate-700">
                                                                #{acc.account_id}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-0.5">
                                                            {acc.account_name}
                                                            {acc.partner_email && (
                                                                <span className="text-blue-400 ml-2">• IB: {acc.partner_email}</span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className="font-bold text-white">{formatUsd(acc.initial_balance, 0)}</div>
                                                        <div className="text-xs text-slate-400">
                                                            Fee: <span className="text-emerald-400 font-semibold">{formatUsd(acc.purchase_price)}</span>
                                                            {' '}• 15% IB: {formatUsd(acc.upfront_commission_paid)}
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className="font-extrabold text-white">{formatUsd(acc.combined_equity)}</div>
                                                        <div className="text-[11px] text-slate-400">
                                                            All-Time HWM: <span className="text-slate-200">{formatUsd(acc.high_water_mark)}</span>
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className="flex justify-between text-xs mb-1">
                                                            <span className="text-emerald-400 font-bold">{formatUsd(acc.profit_target_equity, 0)}</span>
                                                            <span className="text-slate-400">{targetProgressPct.toFixed(0)}%</span>
                                                        </div>
                                                        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-emerald-500 transition-all"
                                                                style={{ width: `${targetProgressPct}%` }}
                                                            />
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className={`font-bold ${dailyDdPct >= 3.2 ? 'text-red-400' : 'text-slate-200'}`}>
                                                            {dailyDdPct.toFixed(2)}% / 4.00%
                                                        </div>
                                                        <div className="text-[11px] text-slate-400">
                                                            Floor: {formatUsd(acc.daily_breach_floor_equity)}
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className={`font-bold ${maxDdPct >= 5.5 ? 'text-red-400' : 'text-slate-200'}`}>
                                                            {maxDdPct.toFixed(2)}% / 7.00%
                                                        </div>
                                                        <div className="text-[11px] text-slate-400">
                                                            Floor: {formatUsd(acc.overall_breach_floor_equity)}
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${tradingDays >= 3
                                                                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                                                    : 'bg-slate-800 text-slate-300 border border-slate-700'
                                                                }`}>
                                                                <Calendar size={11} className="inline mr-1" />
                                                                {tradingDays}/3 Days
                                                            </span>

                                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${largestTradePct > 40
                                                                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                                                    : 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                                                                }`}>
                                                                Max Trade: {largestTradePct.toFixed(1)}% (≤40%)
                                                            </span>

                                                            {acc.rule_violations && acc.rule_violations.hedging_detected && (
                                                                <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                                                                    Hedging Flag
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="py-3.5 px-4">
                                                        {renderPioneerStatusBadge(acc.status)}
                                                    </td>

                                                    <td className="py-3.5 px-4 text-right">
                                                        <button
                                                            onClick={() => openEditModal(acc)}
                                                            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                                                            title="Edit Pioneer Baseline / Status"
                                                        >
                                                            <Edit3 size={15} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Baseline / Status Modal */}
            {editingAccount && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                            <div>
                                <h3 className="text-lg font-bold text-white">
                                    Edit {editingAccount.program_type === '10x' ? '10X' : 'Pioneer'} Account #{editingAccount.account_id}
                                </h3>
                                <p className="text-xs text-slate-400">{editingAccount.email}</p>
                            </div>
                            <button onClick={() => setEditingAccount(null)} className="text-slate-400 hover:text-white">
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveBaseline} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                                    Initial Starting Balance ($B0)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editForm.initial_balance}
                                    onChange={(e) => setEditForm({ ...editForm, initial_balance: e.target.value })}
                                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                                    required
                                />
                                {editingAccount.program_type === '10x' && editForm.initial_balance && (
                                    <p className="text-[11px] text-amber-400 mt-1">
                                        Static 10% Breach Floor (0.90 × B0): {formatUsd(Number(editForm.initial_balance) * 0.90)}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                                    Client Purchase / Deposit Price ($ USD)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editForm.purchase_price}
                                    onChange={(e) => setEditForm({ ...editForm, purchase_price: e.target.value })}
                                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                                    Account Status
                                </label>
                                <select
                                    value={editForm.status}
                                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                                >
                                    {editingAccount.program_type === '10x' ? (
                                        <>
                                            <option value="active">Active / Healthy</option>
                                            <option value="warning_8pct">8% Drawdown Warning</option>
                                            <option value="breached_pending_liquidation">10% Breached (Pending Liquidation)</option>
                                            <option value="liquidated">Liquidated (Credit Withdrawn)</option>
                                            <option value="withdrawn">Withdrawn LP</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="active">Active (In Evaluation)</option>
                                            <option value="passed_evaluation">Passed Evaluation (9% Target)</option>
                                            <option value="warning_daily">Daily Drawdown Warning</option>
                                            <option value="warning_max_dd">Max Drawdown Warning</option>
                                            <option value="breached_daily_4pct">Breached 4% Daily Drawdown</option>
                                            <option value="breached_max_7pct">Breached 7% Max Drawdown</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                                    Referring Partner (IB Email)
                                </label>
                                <input
                                    type="email"
                                    value={editForm.partner_email}
                                    onChange={(e) => setEditForm({ ...editForm, partner_email: e.target.value })}
                                    placeholder="Optional partner email"
                                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setEditingAccount(null)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FundedAccountsPage;

